import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { CustomErrorType, ErrorTypeEnum } from '@ld.consumer-portal/models';

@Injectable({
  providedIn: 'root',
})
export class CustomErrorModalService {
  private readonly router = inject(Router);
  private readonly translocoService = inject(TranslocoService);

  readonly isOpen = signal<boolean>(false);

  private readonly currentError = signal<CustomErrorType | null>(null);

  readonly title = computed(() => {
    const error = this.currentError();
    if (!error) return 'Error';
    return error.errorTitleKey ? this.translocoService.translate(error.errorTitleKey) : error.errorTitle;
  });

  readonly message = computed(() => {
    const error = this.currentError();
    if (!error) return '';
    return error.errorMessageKey ? this.translocoService.translate(error.errorMessageKey) : error.errorMessage;
  });

  readonly icon = computed(() => (this.currentError()?.errorType === ErrorTypeEnum.Warning ? 'warning' : 'error'));

  readonly primaryBtnText = computed(() => {
    const error = this.currentError();
    if (!error) return 'OK';
    return error.btnTextKey ? this.translocoService.translate(error.btnTextKey) : error.btnText;
  });

  readonly secondaryBtnText = computed(() => {
    const error = this.currentError();
    if (!error?.btnTextSecond) return null;
    return error.btnTextSecondKey ? this.translocoService.translate(error.btnTextSecondKey) : error.btnTextSecond;
  });

  private readonly btnLink = computed(() => this.currentError()?.btnLink ?? null);

  show(customError: CustomErrorType): void {
    this.currentError.set(customError);
    this.isOpen.set(true);
  }

  close(): void {
    this.isOpen.set(false);
    this.currentError.set(null);
  }

  onPrimaryClick(): void {
    const link = this.btnLink();
    if (link) {
      this.router.navigate([link]);
    }
    this.close();
  }

  onSecondaryClick(): void {
    this.close();
  }
}
